import { ICommonInitialState } from "@/shared/types/storeTypes";
import { createAsyncThunkInstance } from "@/store/createAsyncThunkInstance";
import api from "@/api";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  IExportVacancy,
  VacancyUpdateRequest,
  IExportVacancyFields,
  VacancyViewRequest,
  VacanciesRequest,
  VacanciesResponse,
} from "@/api/vacancy/types";
import { AxiosError } from "axios";
import { RecruitersRequest, RecruitersResponse } from "@/api/recruiters/types";
import { Recruiter } from "@/model/recruiters";
import { Vacancy } from "@/model/vacancy";

interface IVacancyState extends ICommonInitialState {
  vacancies: VacanciesResponse | null;
  currentVacancy: Vacancy | null;
  recruiters: Recruiter[];
  recruitersByDepartment: Recruiter[];
  exportVacancyFields: IExportVacancyFields | null;
}

const initialState: IVacancyState = {
  vacancies: null,
  currentVacancy: null,
  recruiters: [],
  recruitersByDepartment: [],
  exportVacancyFields: null,
  isLoading: false,
  error: null,
};

export const fetchAllVacancies = createAsyncThunkInstance<VacanciesResponse, VacanciesRequest>(
  "vacancy/fetchAllVacancies",
  async (data?: VacanciesRequest) => {
    const response = await api.vacancy.vacancies(data);
    return response;
  },
);

export const fetchSingleVacancy = createAsyncThunkInstance<Vacancy, VacancyViewRequest>("vacancy/fetchCurrentVacancy", api.vacancy.vacancy);

export const fetchRecruiters = createAsyncThunkInstance<RecruitersResponse, RecruitersRequest | undefined>("vacancy/fetchRecruiters", (params) =>
  api.recruiters.recruiters({ ...params, recruiter: params?.recruiter ?? true }),
);

export const fetchRecruitersByDepartment = createAsyncThunkInstance<RecruitersResponse, RecruitersRequest>(
  "vacancy/fetchRecruitersByDepartment",
  api.recruiters.recruiters,
);

export const fetchUpdateVacancy = createAsyncThunk<Vacancy, VacancyUpdateRequest, { rejectValue: AxiosError }>(
  "vacancy/fetchUpdateVacancy",
  async ({ params, body }, { rejectWithValue }) => {
    try {
      const { data } = await api.vacancy.updateVacancy({ params: { id: params.id }, body });
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  },
);

export const fetchExportVacancyFields = createAsyncThunkInstance<IExportVacancyFields, number>(
  "vacancy/fetchExportVacancyFields",
  api.vacancy.exportVacancyFields,
);

export const fetchExportVacancy = createAsyncThunkInstance<Blob, { id: number; params: IExportVacancy }>(
  "vacancy/fetchExportVacancy",
  ({ id, params }) => api.vacancy.exportVacancy(id, params),
);

const vacancySlice = createSlice({
  name: "vacancy",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSingleVacancy.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleVacancy.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVacancy = action.payload;
      })
      .addCase(fetchRecruiters.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecruiters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recruiters = action.payload;
      })
      .addCase(fetchAllVacancies.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllVacancies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vacancies = action.payload;
      })
      .addCase(fetchRecruitersByDepartment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRecruitersByDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.recruitersByDepartment = action.payload;
      })
      .addCase(fetchExportVacancyFields.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExportVacancyFields.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exportVacancyFields = action.payload;
      });
  },
});

export const vacancyReducer = vacancySlice.reducer;
